import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { ApprovalStatus, StatusOption, TrashItem, Video } from "@/lib/api-types";

const TRASH_KEY = ["trash"] as const;

/** One version row from GET /editor/videos/:id/versions. */
export type VideoVersion = {
  id: string;
  title: string;
  versionNumber: number;
  assetType: "video" | "audio" | "image";
  approvalStatus: ApprovalStatus;
  fileSizeBytes: number;
  durationSeconds: number | null;
  uploaderName: string | null;
  commentCount: number;
  createdAt: string | null;
  cdnUrl: string | null;
  shareToken: string | null;
  downloadAllowed: boolean | null;
};

export type VideoVersionsResponse = {
  rootVideoId: string;
  projectId: string;
  /** Unified Status (migration 0023) — asset-level, shared across all versions. */
  statusOptions?: StatusOption[];
  status?: StatusOption | null;
  versions: VideoVersion[];
};

/**
 * GET /api/editor/videos/:id/versions — all versions in the stack this id
 * belongs to (accepts a root OR child id), ordered by versionNumber ASC.
 * Powers the versions page.
 */
export function useVideoVersions(videoId: string | undefined) {
  return useQuery<VideoVersionsResponse>({
    queryKey: ["videos", videoId, "versions"],
    queryFn: () => api.get<VideoVersionsResponse>(`/editor/videos/${videoId}/versions`),
    enabled: !!videoId,
    staleTime: 10_000,
  });
}

/**
 * DELETE /api/editor/videos/:id — soft-trash a single version. Backend handles
 * stack semantics: deleting the latest auto-promotes the previous (latest is
 * computed at read time); deleting the root re-anchors to the next-oldest.
 * `rootId` is only used to refresh the right versions query afterward.
 */
export function useDeleteVersion(rootId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { id: string }>({
    mutationFn: ({ id }) => api.delete<{ ok: true }>(`/editor/videos/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["videos", rootId, "versions"] });
      // Grid cards carry versionCount/latest info — refresh every project list.
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: TRASH_KEY });
    },
  });
}

/**
 * GET /api/editor/projects/:id/videos — root videos only (version stacks
 * collapsed, each enriched with versionCount/latestVersionId/cdnUrl). Any
 * workspace role.
 *
 * `folder` scope (migration 0012):
 *   "all"  — every video in the project regardless of folder placement (default)
 *   "root" — only videos at project root (folderId IS NULL)
 *   <id>   — videos directly inside that folder (caller must have folder access)
 */
export function useProjectVideos(
  projectId: string | undefined,
  folder: "all" | "root" | string = "all"
) {
  const qs = folder === "all" ? "" : `?folder=${encodeURIComponent(folder)}`;
  return useQuery<Video[]>({
    queryKey: ["projects", projectId, "videos", folder],
    queryFn: () => api.get<Video[]>(`/editor/projects/${projectId}/videos${qs}`),
    enabled: !!projectId,
    staleTime: 15_000,
  });
}

/**
 * GET /api/editor/videos/:id/download — fetches a presigned R2 URL that
 * carries Content-Disposition: attachment. Solves the cross-origin download
 * problem: linking `<a href={cdnUrl} download>` from lab.imgwala.com to
 * cdn.lab.imgwala.com was ignored by the browser and just previewed the file
 * in a new tab. Backend signs response-content-disposition into the URL so
 * the CDN's response forces a save regardless of origin.
 */
export async function fetchVideoDownloadUrl(videoId: string): Promise<{ downloadUrl: string; filename: string }> {
  return api.get<{ ok: true; downloadUrl: string; filename: string }>(`/editor/videos/${videoId}/download`);
}

/**
 * Trigger a browser download from a signed URL. Same-origin fetch of the URL
 * itself isn't needed — the browser handles Content-Disposition on the
 * response and saves. Just navigate to the URL via a hidden anchor click.
 * `<a>` is created off-DOM so the caller doesn't have to clean up.
 */
export function triggerDownload(url: string, suggestedFilename?: string) {
  const a = document.createElement("a");
  a.href = url;
  // download attr is a HINT — the server's Content-Disposition wins. Include
  // the suggestion anyway so browsers that don't parse the header (rare) at
  // least name the file correctly.
  if (suggestedFilename) a.download = suggestedFilename;
  a.rel = "noopener";
  a.click();
}

/**
 * POST /api/editor/videos/:id/duplicate — server-side R2 copy + fresh row.
 *
 * Contract locked with user 2026-07-18:
 *   - Fresh standalone V1 in a new stack (source can be any version, output
 *     is always a new root — no version history preserved).
 *   - File + row only. Comments / annotations / share links / views /
 *     approval status all reset.
 *   - Same project + folder as source. Same assetType + fileSizeBytes.
 *   - Title auto-numbered: "<src> (copy)", "<src> (copy 2)", ...
 *   - Editor of the new row = current user, not the source's uploader.
 *   - R2 cost: full byte copy against the workspace's storage quota.
 *
 * Runtime warning: on large files this call blocks until R2 finishes the
 * server-side copy. Frame the mutation with a loading state on the caller
 * side — don't leave the user staring at a silent menu item.
 */
export function useDuplicateVideo(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (videoId: string) =>
      api.post<{
        ok: true;
        id: string;
        title: string;
        projectId: string;
        folderId: string | null;
        assetType: "video" | "audio" | "image";
      }>(`/editor/videos/${videoId}/duplicate`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", projectId, "videos"] });
    },
  });
}

/**
 * PATCH /api/editor/videos/:id — rename (title only). Perms mirror trash:
 * owner OR FULL_ACCESS+. Applies to a single video row (root or child version);
 * stack versions can carry divergent titles matching Frame.io's per-version
 * label model. Invalidates the project's videos list so the card + detail
 * page pick up the new name.
 */
export function useRenameVideo(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ videoId, title }: { videoId: string; title: string }) =>
      api.patch<{ ok: true; id: string; title: string }>(`/editor/videos/${videoId}`, { title }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", projectId, "videos"] });
    },
  });
}

/**
 * PATCH /api/editor/videos/:id/download — toggles the `downloadAllowed` flag
 * on the ROOT video (share tokens live there). Gated by TOGGLE_DOWNLOAD role
 * or the per-video editorCanToggleDownload flag on the backend.
 */
export function useToggleDownload() {
  return useMutation({
    mutationFn: ({ videoId, downloadAllowed }: { videoId: string; downloadAllowed: boolean }) =>
      api.patch<{ ok: true }>(`/editor/videos/${videoId}/download`, { downloadAllowed }),
  });
}

/**
 * PATCH /api/editor/videos/:id/approval — any workspace role (CHANGE_APPROVAL_STATUS
 * is granted to all 4 tiers). Body: { approvalStatus }.
 */
export function useUpdateVideoApproval(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ videoId, approvalStatus }: { videoId: string; approvalStatus: ApprovalStatus }) =>
      api.patch<{ ok: true; approvalStatus: ApprovalStatus }>(`/editor/videos/${videoId}/approval`, {
        approvalStatus,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", projectId, "videos"] });
    },
  });
}

/**
 * DELETE /api/editor/videos/:id — SOFT delete, moves video to trash for 10 days
 * (see worker/lib/trash.js). Restorable via POST /api/editor/videos/:id/restore.
 * Auto-purged by cron after TRASH_TTL_MS.
 */
export function useDeleteVideo(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (videoId: string) => api.delete<{ ok: true }>(`/editor/videos/${videoId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", projectId, "videos"] });
      // Refresh any open versions page (deleting a version from its card menu).
      qc.invalidateQueries({ queryKey: ["videos"] });
    },
  });
}

/**
 * DELETE /api/editor/videos/:id/permanent — PERMANENT hard delete. Cascades
 * annotation blobs + rows, comment rows, guest rows, video R2 blob, video row.
 * Cannot be undone. State-agnostic — works on live videos AND trashed videos
 * (relaxed 2026-07-08 — type-to-confirm UI gate is the safeguard now).
 */
export function useDeleteVideoPermanent(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (videoId: string) =>
      api.delete<{ ok: true }>(`/editor/videos/${videoId}/permanent`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", projectId, "videos"] });
      qc.invalidateQueries({ queryKey: ["videos"] });
      qc.invalidateQueries({ queryKey: TRASH_KEY });
    },
  });
}

/**
 * GET /api/editor/trash — unified list of trashed videos + projects.
 * Backend returns rows with `kind` discriminator + `daysLeft` countdown.
 */
export function useTrashList() {
  return useQuery<TrashItem[]>({
    queryKey: TRASH_KEY,
    queryFn: () => api.get<TrashItem[]>("/editor/trash"),
    staleTime: 15_000,
  });
}

/**
 * POST /api/editor/videos/:id/restore — un-trash a single video. Clears
 * deletedAt/trashExpiresAt/deletionType + sets retentionAnchorAt=now so the
 * retention clock restarts for this one video only.
 */
export function useRestoreVideo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (videoId: string) =>
      api.post<{ ok: true }>(`/editor/videos/${videoId}/restore`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TRASH_KEY });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}
